/**
 * documentIdentity.js — Source de vérité pour l'identité et la résolution des documents.
 *
 * ⚠️ FICHIER PARTAGÉ : une copie identique existe dans MyConcours-web/services/.
 * Toute modification doit être répercutée dans les DEUX projets.
 *
 * ---------------------------------------------------------------------------
 * PROBLÈME RÉSOLU
 * ---------------------------------------------------------------------------
 * Avant, une épreuve de concours était identifiée par (titre + annee + filiere).
 * L'admin corrigeait une faute de frappe dans le titre → le lien avec les achats
 * déjà payés était rompu → l'utilisateur perdait l'accès à un document acheté.
 *
 * Désormais :
 *   1. Chaque épreuve porte un `epreuveId` STABLE qui ne change jamais.
 *   2. La résolution se fait en cascade : epreuveId → texte normalisé → legacy.
 *   3. Quand une résolution réussit via un fallback, l'appelant réécrit
 *      l'epreuveId dans le purchase (auto-réparation silencieuse).
 *
 * ---------------------------------------------------------------------------
 * DETTE HISTORIQUE ABSORBÉE ICI
 * ---------------------------------------------------------------------------
 * - PublishDocumentScreen écrivait l'URL dans `document`, EditDocumentScreen
 *   dans `url`. Les deux sont lus, `url` prioritaire (voir readEpreuveUrl).
 * - Le mobile lisait `url || document`, le web `document || url` : ordre
 *   inversé → fichiers différents servis selon la plateforme. Unifié ici.
 */

// ===========================================================================
// IDENTITÉ
// ===========================================================================

/**
 * Génère un identifiant stable pour une épreuve.
 * Format identique à makeId() des écrans admin pour rester homogène.
 */
export const makeEpreuveId = () =>
  `ep_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Normalise une valeur pour comparaison tolérante.
 * Neutralise : casse, accents, espaces multiples, espaces de bord.
 * "  Épreuve  DE   Maths " et "epreuve de maths" deviennent identiques.
 */
export const normalizeKey = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // supprime les diacritiques
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Clé de repli composite, utilisée quand aucun epreuveId n'est disponible.
 */
export const legacyEpreuveKey = (titre, annee, filiere) =>
  `${normalizeKey(titre)}|${normalizeKey(annee)}|${normalizeKey(filiere)}`;

/**
 * Lit l'URL d'une épreuve en tolérant les deux champs historiques.
 * ORDRE UNIFIÉ mobile + web : `url` d'abord (écrit par l'édition, donc le
 * plus récent), puis `document` (écrit par la publication initiale).
 */
export const readEpreuveUrl = (epreuve) => {
  if (!epreuve) return null;
  if (typeof epreuve === 'string') return epreuve;
  if (typeof epreuve.url === 'string' && epreuve.url) return epreuve.url;
  if (typeof epreuve.document === 'string' && epreuve.document) return epreuve.document;
  return null;
};

/**
 * Garantit qu'une épreuve possède un epreuveId.
 * Idempotent : ne réécrit jamais un id existant.
 */
export const ensureEpreuveId = (epreuve) => {
  if (!epreuve || typeof epreuve !== 'object') return epreuve;
  if (epreuve.epreuveId) return epreuve;
  // `id` était déjà généré par PublishDocumentScreen : on le promeut
  // plutôt que d'en créer un nouveau, pour préserver les liens existants.
  return { ...epreuve, epreuveId: epreuve.id || makeEpreuveId() };
};

/**
 * Applique ensureEpreuveId à toute la structure d'un concours
 * (documents plats ET documents imbriqués dans les groupes).
 */
export const ensureConcoursIds = (data) => {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };

  if (Array.isArray(next.documents)) {
    next.documents = next.documents.map(ensureEpreuveId);
  }
  if (Array.isArray(next.groups)) {
    next.groups = next.groups.map((group) => ({
      ...group,
      id: group.id || makeEpreuveId(),
      documents: Array.isArray(group.documents)
        ? group.documents.map(ensureEpreuveId)
        : [],
    }));
  }
  return next;
};

// ===========================================================================
// RÉSOLUTION EN CASCADE
// ===========================================================================

/**
 * Retrouve une épreuve dans un concours, quelle que soit son ancienneté.
 *
 * Stratégies, dans l'ordre :
 *   'id'       — match exact sur epreuveId (ou id legacy). Fiable à 100 %.
 *   'exact'    — match sur titre/annee/filiere normalisés.
 *   'partial'  — match sur titre + annee (la filière a pu être renommée).
 *
 * @returns {{epreuve, url, groupId, strategy}|null}
 *          `strategy !== 'id'` signale à l'appelant qu'une auto-réparation
 *          est possible.
 */
export const resolveEpreuve = (concoursData, criteria = {}) => {
  if (!concoursData) return null;

  const { epreuveId, titre, annee, filiere, groupId } = criteria;

  // Aplatit toutes les épreuves en conservant leur groupe d'origine.
  const pool = [];
  if (Array.isArray(concoursData.groups)) {
    for (const group of concoursData.groups) {
      for (const ep of group?.documents || []) {
        pool.push({ epreuve: ep, groupId: group.id });
      }
    }
  }
  if (Array.isArray(concoursData.documents)) {
    for (const ep of concoursData.documents) {
      pool.push({ epreuve: ep, groupId: null });
    }
  }
  if (pool.length === 0) return null;

  const hit = (entry, strategy) => ({
    epreuve: entry.epreuve,
    url: readEpreuveUrl(entry.epreuve),
    groupId: entry.groupId,
    strategy,
  });

  // — Stratégie 1 : identifiant stable ------------------------------------
  if (epreuveId) {
    const found = pool.find(
      (e) => e.epreuve?.epreuveId === epreuveId || e.epreuve?.id === epreuveId
    );
    if (found) return hit(found, 'id');
  }

  // — Stratégie 2 : texte normalisé ---------------------------------------
  const wanted = legacyEpreuveKey(titre, annee, filiere);
  if (wanted !== '||') {
    // Si un groupId est fourni, on le privilégie sans s'y enfermer :
    // l'admin a pu déplacer l'épreuve d'un groupe à l'autre.
    const ordered = groupId
      ? [...pool].sort((a, b) => (b.groupId === groupId) - (a.groupId === groupId))
      : pool;

    const found = ordered.find(
      (e) =>
        legacyEpreuveKey(
          e.epreuve?.titre,
          e.epreuve?.annee,
          e.epreuve?.filiere
        ) === wanted
    );
    if (found) return hit(found, 'exact');
  }

  // — Stratégie 3 : titre + année (filière renommée) -----------------------
  if (titre && annee) {
    const found = pool.find(
      (e) =>
        normalizeKey(e.epreuve?.titre) === normalizeKey(titre) &&
        normalizeKey(e.epreuve?.annee) === normalizeKey(annee)
    );
    if (found) return hit(found, 'partial');
  }

  // — Stratégie 4 : année + filière (TITRE renommé) ------------------------
  // Cas très courant : l'admin reformule un titre ("Maths" → "Mathématiques
  // Générales"). Année + filière identifient l'épreuve de façon quasi unique
  // au sein d'un concours ; on n'accepte toutefois le résultat que s'il est
  // NON AMBIGU, pour ne jamais servir la mauvaise épreuve à un acheteur.
  if (annee && filiere) {
    const matches = pool.filter(
      (e) =>
        normalizeKey(e.epreuve?.annee) === normalizeKey(annee) &&
        normalizeKey(e.epreuve?.filiere) === normalizeKey(filiere)
    );
    if (matches.length === 1) return hit(matches[0], 'renamed');
  }

  return null;
};

// ===========================================================================
// CORRESPONDANCE ACHAT ↔ ÉPREUVE
// ===========================================================================

/**
 * Détermine si un achat correspond à une épreuve donnée.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI CETTE FONCTION EXISTE
 * ---------------------------------------------------------------------------
 * L'app comparait les achats aux épreuves par TEXTE BRUT :
 *
 *   p.titre === docItem.titre && p.annee === docItem.annee && ...
 *
 * Conséquence : dès que l'admin corrigeait un titre, l'achat ne « matchait »
 * plus. L'utilisateur qui avait déjà payé voyait de nouveau « Acheter » —
 * et pouvait payer une SECONDE FOIS le même document.
 *
 * Cette fonction applique la même cascade que resolveEpreuve :
 *   1. epreuveId (identité stable) — fiable à 100 %
 *   2. texte normalisé (accents, casse, espaces neutralisés)
 *   3. année + filière, quand le titre a été reformulé
 *
 * Elle est DÉLIBÉRÉMENT permissive : en cas de doute, mieux vaut considérer
 * l'achat comme valide (l'utilisateur accède à son document) que de lui
 * refaire payer ce qu'il possède déjà.
 *
 * @param {object} purchase  Achat enregistré (collection `purchases`).
 * @param {object} epreuve   Épreuve affichée à l'écran.
 * @param {object} [ctx]     { documentId, groupId } contexte de l'écran.
 * @returns {boolean}
 */
export const purchaseMatchesEpreuve = (purchase, epreuve, ctx = {}) => {
  if (!purchase || !epreuve) return false;

  // Le document parent doit toujours correspondre.
  if (ctx.documentId && purchase.documentId !== ctx.documentId) return false;

  // — 1. Identité stable ---------------------------------------------------
  const epId = epreuve.epreuveId || epreuve.id;
  if (purchase.epreuveId && epId && purchase.epreuveId === epId) return true;

  // — 2. Texte normalisé ---------------------------------------------------
  // Le groupId n'est PAS éliminatoire : l'admin peut déplacer une épreuve
  // d'un groupe à l'autre sans que l'achat cesse d'être valide.
  //
  // Garde-fou : une clé entièrement vide ('||') ne prouve rien — deux
  // enregistrements sans métadonnées matcheraient à tort et offriraient
  // un document non payé.
  const cleAchat = legacyEpreuveKey(purchase.titre, purchase.annee, purchase.filiere);
  const cleEpreuve = legacyEpreuveKey(epreuve.titre, epreuve.annee, epreuve.filiere);
  if (cleAchat !== '||' && cleAchat === cleEpreuve) return true;

  // — 3. Année + filière (titre reformulé) ---------------------------------
  const anneeOk =
    normalizeKey(purchase.annee) === normalizeKey(epreuve.annee) &&
    normalizeKey(purchase.annee) !== '';
  const filiereOk =
    normalizeKey(purchase.filiere) === normalizeKey(epreuve.filiere) &&
    normalizeKey(purchase.filiere) !== '';
  if (anneeOk && filiereOk) return true;

  return false;
};

/**
 * Cherche, parmi les achats d'un utilisateur, celui qui couvre une épreuve.
 * @returns {object|null} l'achat trouvé, ou null.
 */
export const findMatchingPurchase = (purchases, epreuve, ctx = {}) => {
  if (!Array.isArray(purchases)) return null;
  return purchases.find((p) => purchaseMatchesEpreuve(p, epreuve, ctx)) || null;
};

// ===========================================================================
// ARCHIVAGE
// ===========================================================================

/**
 * Un document archivé disparaît du catalogue mais reste lisible par ceux
 * qui l'ont acheté. `archived === true` est la seule source de vérité.
 */
export const isArchived = (docData) => docData?.archived === true;

/**
 * Filtre appliqué à toutes les listes publiques (catalogue, recherche,
 * accueil). Les achats, eux, ignorent volontairement ce filtre.
 */
export const isVisibleInCatalog = (docData) => {
  if (!docData) return false;
  if (isArchived(docData)) return false;
  return docData.status !== 'inactive';
};

export default {
  makeEpreuveId,
  normalizeKey,
  legacyEpreuveKey,
  readEpreuveUrl,
  ensureEpreuveId,
  ensureConcoursIds,
  resolveEpreuve,
  purchaseMatchesEpreuve,
  findMatchingPurchase,
  isArchived,
  isVisibleInCatalog,
};
